// Device picker: shown on each login. The user picks PC or Mobile; the
// choice toggles whether the visible mobile-button-bar (touch buttons)
// gets shown during battle. PC users see no buttons (keyboard handlers
// continue to work as normal).
//
// We use sessionStorage so the picker shows once per browser session
// rather than literally every page navigation — that's close enough to
// "every time you log on" for practical purposes and saves the user
// from re-picking every modal close.

const DEVICE_STORAGE_KEY = 'snordeluxe_device_choice'; // 'pc' | 'mobile'

export function getDeviceChoice() {
    try { return sessionStorage.getItem(DEVICE_STORAGE_KEY); } catch (_) { return null; }
}

export function isMobileChoiceActive() {
    return getDeviceChoice() === 'mobile';
}

function setDeviceChoice(choice) {
    try { sessionStorage.setItem(DEVICE_STORAGE_KEY, choice); } catch (_) {}
    window.__SNORDELUXE_DEVICE_CHOICE__ = choice;
    document.body.dataset.device = choice;
    document.dispatchEvent(new CustomEvent('snordeluxe-device-changed', { detail: { choice } }));
}

function hideModal(modal) {
    if (!modal) return;
    modal.style.display = 'none';
    modal.classList.remove('show');
}

// Show the picker. Returns a promise resolving to the chosen device,
// or null if no DOM is present yet.
export function showDevicePicker() {
    const modal = document.getElementById('devicePickerModal');
    if (!modal) return Promise.resolve(null);
    return new Promise((resolve) => {
        modal.style.display = 'flex';
        const buttons = modal.querySelectorAll('.device-picker-btn');
        const onClick = (e) => {
            const choice = e.currentTarget.getAttribute('data-device');
            if (choice !== 'pc' && choice !== 'mobile') return;
            buttons.forEach(b => b.removeEventListener('click', onClick));
            setDeviceChoice(choice);
            hideModal(modal);
            resolve(choice);
        };
        buttons.forEach(b => b.addEventListener('click', onClick));
    });
}

function isTestModeActive() {
    try {
        return typeof window !== 'undefined' && !!window.__SNORDELUXE_TEST_USER__;
    } catch (_) { return false; }
}

function isPickerForcedInTest() {
    try {
        return typeof window !== 'undefined' && !!window.__SNORDELUXE_TEST_SHOW_DEVICE_PICKER__;
    } catch (_) { return false; }
}

// Called once on each login. If a choice is already stored for this
// session, skip the prompt and just re-publish it. Tests get an
// auto-PC default unless they explicitly opt in to seeing the picker
// via window.__SNORDELUXE_TEST_SHOW_DEVICE_PICKER__ = true (set by
// fixtures before navigation).
export function promptDeviceOnLogin() {
    const existing = getDeviceChoice();
    if (existing) {
        document.body.dataset.device = existing;
        window.__SNORDELUXE_DEVICE_CHOICE__ = existing;
        document.dispatchEvent(new CustomEvent('snordeluxe-device-changed', { detail: { choice: existing } }));
        return Promise.resolve(existing);
    }
    if (isTestModeActive() && !isPickerForcedInTest()) {
        try { sessionStorage.setItem('snordeluxe_device_choice', 'pc'); } catch (_) {}
        window.__SNORDELUXE_DEVICE_CHOICE__ = 'pc';
        document.body.dataset.device = 'pc';
        document.dispatchEvent(new CustomEvent('snordeluxe-device-changed', { detail: { choice: 'pc' } }));
        return Promise.resolve('pc');
    }
    return showDevicePicker();
}
